import { getLanguage, type I18nLanguage } from '@/i18n';
import { type Locale, locales } from '@/utils/locale';
import {
  format as dateFnsFormat,
  formatDate,
  intervalToDuration,
  parseISO,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

export const format = <DateType extends Date>(
  date: DateType | number | string,
  formatStr: string
) =>
  dateFnsFormat(date, formatStr, {
    locale: getDateFnsLocale(),
  });

export const formatFullDate = (date: Date) => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
};

export const formatDay = (date?: string) => {
  return date && formatDate(date, 'd MMM', { locale: getDateFnsLocale() });
};
export const formatYear = (date?: string) => {
  return date && formatDate(date, 'yyyy', { locale: getDateFnsLocale() });
};

export const formatTime = (date?: string) => {
  return date && formatDate(date, 'HH:mm', { locale: getDateFnsLocale() });
};

export const formatDateLLL = (date?: Date) => {
  return date && format(date, 'd LLL');
};

export const getDurationBetweenTimestamps = (
  first?: string,
  second?: string
) => {
  if (!first || !second) {
    return null;
  }

  const timeDiff = new Date(second).getTime() - new Date(first).getTime();

  if (timeDiff > 0) {
    const duration = intervalToDuration({ start: 0, end: timeDiff });
    return `${duration.hours ? duration.hours + 'h:' : ''}${duration.minutes}m`;
  }

  return null;
};

export const formatDuration = (duration: string | undefined) =>
  duration && duration.substring(2).toLowerCase().replace('h', 'h ');

export const formatDateWithDayOfWeek = (dateString?: string) => {
  return dateString && format(parseISO(dateString), 'EEE, d MMM yyyy');
};

export const isTimestampInPast = (
  departureTimeIsoString: string,
  timeZone: string
) => {
  const now = new Date();
  const nowInTimeZone = toZonedTime(now, timeZone);

  return new Date(departureTimeIsoString) < nowInTimeZone;
};

export const getDateFnsLocale = (): Locale => {
  const currentLanguage = getLanguage() as I18nLanguage;

  return locales[currentLanguage];
};
